import * as moment from 'moment';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { KEYS } from '../../../core/service/constants/service.constants';
import { HttpService } from '../../../core/service/services/http.service';
import { StorageSessionService } from '../../../core/storage/services/storage.session.service';
import { QueryParameters } from '../../analytics-core/interfaces/query-parameters.interface';
import { AnalyticsCoreURLService } from '../../analytics-core/services/analytics-core.url.service';
import { ANALYTICS_CONFIGS } from '../constants/analytics.constants';
import { AnalyticsDataModel } from '../models/analytics.datamodel';
import { AnalyticsDataChachingService } from './analytics.datacaching.service';
import { AnalyticsMixpanelService } from './analytics.mixpanel.service';
import { GAM_PRODUCT_SEPERATED_Rs } from '../../analytics/constants/analytics.constants';

@Injectable()
export class AnalyticsService extends HttpService {
	public deprecated_date_avg_position: moment.MomentInput;
	public semGoLiveDate: moment.MomentInput;
	public isGoliveNotStarted: boolean;
	public isCutoffDate: boolean;
	private domainName: string;
	private scrollBarWidth: any;
	private reqDate: any;

	constructor(
		http: HttpClient,
		protected storageSessionService: StorageSessionService,
		router: Router,
		private analyticsDataModel: AnalyticsDataModel,
		private analyticsCoreURLService: AnalyticsCoreURLService,
		private AnalyticsDataChachingService: AnalyticsDataChachingService,
		private analyticsMixpanelService: AnalyticsMixpanelService
	) {
		super(http, storageSessionService, router);
		this.setDomainName();
		this.getSetupData().subscribe((res: any) => {
			if (res.success) {
				this.deprecated_date_avg_position = res.deprecated_date_avg_position;
				this.semGoLiveDate = res.go_live_date_impression_share;
			}
		});
	}

	setDomainName(): void {
		this.domainName = this.analyticsCoreURLService.domainName;
	}

	getDomainName(): string {
		return this.domainName;
	}

	numberFormatter(value: number) {
		return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
	}

	getScrollBarWidth(): any {
		return this.scrollBarWidth;
	}

	setScrollBarWidth(width: any): any {
		this.scrollBarWidth = width;
	}

	processWidgetRequest(
		qKey?: string,
		columns?: any,
		order_by?: any,
		uniqueOverwriteID?: string,
		filterGamCampaigns?: string
	): any {
		let { makeApi, widgetData } = this.AnalyticsDataChachingService.getWidgetDataStatus(uniqueOverwriteID);
		if (!makeApi && widgetData?.data) {
			return new Observable((observer) => {
				observer.next(widgetData);
				observer.complete();
			});
		} else if (makeApi || (!makeApi && !widgetData?.data)) {
			const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
			if (filterGamCampaigns == 'gamOnly') {
				reqBody.c_ids = reqBody.gam_ids;
			} else if (filterGamCampaigns == 'nonGamOnly') {
				reqBody.c_ids = reqBody.non_gam_ids;
			}

			if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
				return new Observable((observer) => {
					observer.next({ success: false });
					observer.complete();
				});
			}
			reqBody['qkey'] = qKey ? qKey : '';
			reqBody['domain'] = this.getDomainName();
			reqBody['columns'] = columns;
			reqBody['order_by'] = order_by;
			return this.post(this.analyticsCoreURLService.serviceURL + '/analytics_module/process_request', reqBody).pipe(
				map((res) => {
					if (uniqueOverwriteID) {
						this.AnalyticsDataChachingService.setDataInConfig(uniqueOverwriteID, res);
					}
					return res;
				}),
				catchError((error) => this.handleError(error))
			);
		}
	}

	processOverviewRequest(qKey?: string, permissionedQueryKeys?: any[], uniqueOverwriteID?: string): any {
		let { makeApi, widgetData } = this.AnalyticsDataChachingService.getWidgetDataStatus(uniqueOverwriteID);
		if (!makeApi && widgetData?.data) {
			return new Observable((observer) => {
				observer.next(widgetData);
				observer.complete();
			});
		} else if (makeApi || (!makeApi && !widgetData?.data)) {
			const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
			if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
				return new Observable((observer) => {
					observer.next({ success: false });
					observer.complete();
				});
			}
			reqBody['qkey'] = qKey ? qKey : '';
			reqBody['domain'] = this.getDomainName();
			reqBody['permissionedQueryKeys'] =
				permissionedQueryKeys && permissionedQueryKeys.length ? JSON.stringify(permissionedQueryKeys) : false;

			return this.post(
				this.analyticsCoreURLService.serviceURL + '/analytics_module/get_overview_data_by_permissioned_product',
				reqBody
			).pipe(
				map((res) => {
					if (uniqueOverwriteID) {
						this.AnalyticsDataChachingService.setDataInConfig(uniqueOverwriteID, res);
					}
					return res;
				}),
				catchError((error) => this.handleError(error))
			);
		}
	}

	processCustomServiceRequest(
		qKey?: string,
		ad_id?: string,
		ad_type?: string,
		show_creative_perf_graph?: boolean,
		ad_ids?: Array<number>,
		uniqueOverwriteID?: string,
		parent_id?: number
	): any {
		let { makeApi, widgetData } = this.AnalyticsDataChachingService.getWidgetDataStatus(uniqueOverwriteID);
		if (!makeApi && widgetData?.data) {
			return new Observable((observer) => {
				observer.next(widgetData);
				observer.complete();
			});
		} else if (makeApi || (!makeApi && !widgetData?.data)) {
			const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
			if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
				return new Observable((observer) => {
					observer.next({ success: false });
					observer.complete();
				});
			}
			reqBody['domain'] = this.getDomainName();
			if (ad_id) {
				reqBody['ad_id'] = ad_id;
				reqBody['ad_type'] = ad_type;
				reqBody['show_creative_perf_graph'] = show_creative_perf_graph;
			}
			reqBody['ad_ids'] = ad_ids;
			if (parent_id) reqBody['parent_id'] = parent_id;

			return this.post(`${this.analyticsCoreURLService.serviceURL}/analytics_module/${qKey}`, reqBody).pipe(
				map((res) => {
					if (uniqueOverwriteID) {
						this.AnalyticsDataChachingService.setDataInConfig(uniqueOverwriteID, res);
					}
					return res;
				}),
				catchError((error) => this.handleError(error))
			);
		}
	}

	processCustomReporting(attributes: string[], metrics: string[], datasource: string, qKey?: string): any {
		const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
		if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
			return new Observable((observer) => {
				observer.next({ success: false });
				observer.complete();
			});
		}
		reqBody['domain'] = this.getDomainName();
		reqBody['attributes'] = attributes;
		reqBody['metrics'] = metrics;
		if (datasource) {
			reqBody['datasource'] = datasource;
		}

		return this.post(`${this.analyticsCoreURLService.serviceURL}/analytics_module/${qKey}`, reqBody).pipe(
			map((res) => res),
			catchError((error) => this.handleError(error))
		);
	}

	getCreativePerformanceData(qKey?: string, uniqueOverwriteID?: string): any {
		let { makeApi, widgetData } = this.AnalyticsDataChachingService.getWidgetDataStatus(uniqueOverwriteID);
		if (!makeApi && widgetData?.data) {
			return new Observable((observer) => {
				observer.next(widgetData);
				observer.complete();
			});
		} else if (makeApi || (!makeApi && !widgetData?.data)) {
			const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
			if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
				return new Observable((observer) => {
					observer.next({ success: false });
					observer.complete();
				});
			}
			reqBody['qkey'] = qKey ? qKey : '';
			reqBody['domain'] = this.getDomainName();

			return this.post(
				`${this.analyticsCoreURLService.serviceURL}/analytics_module/get_preroll_creative_performance_data`,
				reqBody
			).pipe(
				map((res) => {
					if (uniqueOverwriteID) {
						this.AnalyticsDataChachingService.setDataInConfig(uniqueOverwriteID, res);
					}
					return res;
				}),
				catchError((error) => this.handleError(error))
			);
		}
	}

	getConfigData(url: string): any {
		return new Promise((resolve) => {
			this.get(url)
				.toPromise()
				.then((resp) => {
					resolve(resp);
				});
		});
	}
	getPartnerConfiguration(): any {
		const configName = this.analyticsDataModel.partner.analyticsConfig;
		const partnerConfig = this.http.get(`assets/partner-assets/${configName}/data/configuration.json`);
		return partnerConfig;
	}

	getPartnerConfigFromDefaultConfig(partnerConfig: any): Observable<any> {
		const partnerConfigVersion = partnerConfig['version'] + '_DEFAULT';
		const defaultConfigName = ANALYTICS_CONFIGS[partnerConfigVersion];
		const partnerConfigName = this.analyticsDataModel.partner.analyticsConfig;
		if (defaultConfigName !== partnerConfigName && partnerConfig.useDefaultConfig) {
			return this.http.get(`assets/partner-assets/${defaultConfigName}/data/configuration.json`);
		} else {
			return new Observable<any>((obs) => {
				obs.next(false);
			});
		}
	}

	getSetupData(): any {
		const reqBody = {
			domain: this.getDomainName()
		};
		return this.post(this.analyticsCoreURLService.serviceURL + '/analytics_module/get_setup_data', reqBody).pipe(
			map((res) => res),
			catchError((error) => this.handleError(error))
		);
	}

	getCampaignData(advertiserIds: any[]): any {
		const status = this.analyticsDataModel.config.version === 'R1' ? true : false;
		const start_date = this.analyticsDataModel.requestObjectForProcessRequest['start_date'];
		const end_date = this.analyticsDataModel.requestObjectForProcessRequest['end_date'];
		const reqBody = {
			a_ids: advertiserIds,
			domain: this.getDomainName(),
			end_date,
			filter_by_product: status,
			start_date
		};
		return this.post(this.analyticsCoreURLService.serviceURL + '/analytics_module/get_campaigns', reqBody).pipe(
			map((res: any) => {
				let R_name = this.analyticsDataModel.config.partner.directoryName;
				if (
					R_name !== 'default-r13' &&
					R_name !== 'lee-r13' &&
					GAM_PRODUCT_SEPERATED_Rs.includes(R_name) &&
					!this.analyticsDataModel.setupData.userPermissions['hasGAM']
				) {
					res.campaigns = res.campaigns.filter((row) => {
						return !(
							row.is_gam === 'true' &&
							(row.permission.includes('has_gam_access') || row.permission.includes('has_standalone_gam'))
						);
					});
				}
				return res;
			}),
			catchError((error) => this.handleError(error))
		);
	}

	getBoundsByZCTA(regionIds: any): any {
		const reqBody = {
			domain: this.getDomainName(),
			zcta_id_list: regionIds
		};
		return this.post(this.analyticsCoreURLService.serviceURL + '/analytics_module/get_bounds_of_zctas', reqBody).pipe(
			map((res) => res),
			catchError((error) => this.handleError(error))
		);
	}

	getMapDetails(key: string, regionData: any): any {
		const reqBody = {
			complexity_level: 'max',
			domain: this.getDomainName(),
			memory_limit: '15M',
			region_id: regionData,
			region_type: 'zcta'
		};

		return this.post(`${this.analyticsCoreURLService.serviceURL}/analytics_module/${key}`, reqBody).pipe(
			map((res) => res),
			catchError((error) => this.handleError(error))
		);
	}

	getMapWidgetData(
		key: string,
		product: string,
		producttype?: string,
		uniqueOverwriteID?: string,
		geofencegeovideocallattibutekey?: any,
		withoutgam?: boolean
	): any {
		let { makeApi, widgetData } = this.AnalyticsDataChachingService.getWidgetDataStatus(uniqueOverwriteID);
		makeApi = withoutgam ? true : makeApi;
		if (!makeApi && widgetData?.data) {
			return new Observable((observer) => {
				observer.next(widgetData);
				observer.complete();
			});
		} else if (makeApi || (!makeApi && !widgetData?.data)) {
			const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
			if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
				return new Observable((observer) => {
					observer.next({ success: false });
					observer.complete();
				});
			}
			reqBody['domain'] = this.getDomainName();
			reqBody['product'] = product;
			reqBody['product_type'] = producttype;
			reqBody['call_attribute_key'] = geofencegeovideocallattibutekey
				? geofencegeovideocallattibutekey?.toLocaleLowerCase().trim() == 'true'
				: false;
			reqBody['without_gam'] = withoutgam ? withoutgam : false;
			return this.post(`${this.analyticsCoreURLService.serviceURL}/analytics_module/${key}`, reqBody).pipe(
				map((res) => {
					if (uniqueOverwriteID) {
						this.AnalyticsDataChachingService.setDataInConfig(uniqueOverwriteID, res);
					}
					return res;
				}),
				catchError((error) => this.handleError(error))
			);
		}
	}

	logout(): any {
		return this.get(this.analyticsCoreURLService.serviceURL + '/analytics_module/api_logout').pipe(
			map((res) => {
				this.AnalyticsDataChachingService.removeDataFromConfig();
				return res;
			}),
			catchError((error) => this.handleError(error))
		);
	}

	changePassword(details: { new_password: string; password: string }): any {
		const reqBody = details;
		reqBody['domain'] = this.getDomainName();
		reqBody['email'] = this.analyticsDataModel.user.email;
		return this.post(this.analyticsCoreURLService.serviceURL + '/analytics_module/change_password', reqBody).pipe(
			map((res) => res),
			catchError((error) => this.handleError(error))
		);
	}

	downloadCSV(
		key: string,
		fileName: string,
		columns?: any,
		order_by?: any,
		newtab_key?: string,
		newtab_name?: string
	): any {
		const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
		const domainName = this.getDomainName();
		const auth_token = this.storageSessionService.getToken(KEYS.STORAGE_TOKEN_ID);

		reqBody['qkey'] = key;
		reqBody['domain_name'] = domainName;
		reqBody['auth_token'] = auth_token['f_a_token'];
		reqBody['file_name'] = fileName;
		reqBody['columns'] = JSON.stringify(columns);
		reqBody['order_by'] = JSON.stringify(order_by);
		if (newtab_key != '' && newtab_name != '') {
			reqBody['newtab_key'] = newtab_key;
			reqBody['newtab_name'] = newtab_name;
		}
		this.analyticsMixpanelService.mixpanelWidgetDownloadData('Analytics / ' + fileName + ' CSV Download');
		this.openWindowWithPost(reqBody);
	}

	downloadProductXLSX(key: string, fileName: string, productDownloadConfigData: string, newTabDetails?: any): any {
		const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
		const domainName = this.getDomainName();
		const auth_token = this.storageSessionService.getToken(KEYS.STORAGE_TOKEN_ID);

		reqBody['domain_name'] = domainName;
		reqBody['auth_token'] = auth_token['f_a_token'];
		reqBody['productDownloadConfigData'] = productDownloadConfigData;
		reqBody['file_name'] = fileName;
		reqBody['newTabDetails'] = JSON.stringify(newTabDetails);
		this.productDownloadPost(reqBody);
	}

	openWindowWithPost(data: { [x: string]: string }): any {
		const form = document.createElement('form');
		form.target = '_blank';
		form.method = 'POST';
		form.action = this.analyticsCoreURLService.serviceURL + '/analytics_module/download_csv/';
		form.style.display = 'none';

		for (const key in data) {
			if (key) {
				const input = document.createElement('input');
				input.type = 'hidden';
				input.name = key;
				input.value = data[key];
				form.appendChild(input);
			}
		}
		document.body.appendChild(form);
		form.submit();
		document.body.removeChild(form);
	}

	productDownloadPost(data: { [x: string]: string }): any {
		const form = document.createElement('form');
		form.target = '_blank';
		form.method = 'POST';
		form.action = this.analyticsCoreURLService.serviceURL + '/analytics_module/download_all_widgets_by_products_xlsx/';
		form.style.display = 'none';

		for (const key in data) {
			if (key) {
				const input = document.createElement('input');
				input.type = 'hidden';
				input.name = key;
				input.value = data[key];
				form.appendChild(input);
			}
		}
		document.body.appendChild(form);
		form.submit();
		document.body.removeChild(form);
	}

	getNetworkData(qKey?: string, networkId?: string): any {
		const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
		if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
			return new Observable((observer) => {
				observer.next({ success: false });
				observer.complete();
			});
		}
		reqBody['domain'] = this.getDomainName();
		reqBody['network_id'] = networkId ? networkId : '';

		return this.post(`${this.analyticsCoreURLService.serviceURL}/analytics_module/${qKey}`, reqBody).pipe(
			map((res) => res),
			catchError((error) => this.handleError(error))
		);
	}

	checkDateRange(): void {
		const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
		const start_date = reqBody['start_date'];
		const end_date = reqBody['end_date'];
		this.isGoliveNotStarted = moment(end_date).isBefore(this.semGoLiveDate);
		this.isCutoffDate =
			moment(start_date).isAfter(this.deprecated_date_avg_position) ||
			moment(start_date).isSame(this.deprecated_date_avg_position);
	}

	processProductsRequest(qKey?: string, columns?: any, product?: string): any {
		const reqBody = this.analyticsDataModel.requestObjectForProcessRequest;
		if (reqBody.a_ids.length === 0 || reqBody.c_ids.length === 0) {
			return new Observable((observer) => {
				observer.next({ success: false });
				observer.complete();
			});
		}
		reqBody['qkey'] = qKey ? qKey : '';
		reqBody['domain'] = this.getDomainName();
		reqBody['columns'] = columns ? columns : [];
		reqBody['product'] = product ? product : false;

		return this.post(
			this.analyticsCoreURLService.serviceURL + '/analytics_module/get_daily_stats_data_by_permissioned_product',
			reqBody
		).pipe(
			map((res) => res),
			catchError((error) => this.handleError(error))
		);
	}

	processQueryParamsDateRanges(obj: QueryParameters): any {
		const selectedDates = {
			endDate: moment(moment().toISOString()),
			startDate: moment(moment().subtract(1, 'months').toISOString())
		};
		if (obj && obj.end_date !== '') {
			const qpEndDate = moment(obj.end_date);
			selectedDates.endDate =
				qpEndDate.format('MM-DD-YYYY') !== moment(selectedDates.endDate).format('MM-DD-YYYY') &&
				qpEndDate < moment(selectedDates.endDate) &&
				qpEndDate > moment(obj.start_date)
					? moment(obj.end_date)
					: selectedDates.endDate;
		}
		if (obj && obj.start_date !== '' && obj.start_date !== undefined) {
			selectedDates.startDate =
				moment(obj.start_date).format('MM-DD-YYYY') !== moment(selectedDates.startDate).format('MM-DD-YYYY') &&
				moment(obj.start_date) <= moment(selectedDates.endDate)
					? moment(obj.start_date)
					: selectedDates.startDate;
		}
		return selectedDates;
	}
}
